import { create } from 'zustand';
import type { OmniSuggestedVideoMode } from '@/lib/conversations/omni-reference-task-mismatch';

export type OmniMismatchFallbackNotice = {
  title: string;
  description: string;
  toast: (props: {
    title?: string;
    description?: string;
    variant?: 'default' | 'destructive';
  }) => unknown;
};

type PendingErrorMismatch = {
  suggestedMode: OmniSuggestedVideoMode;
  nonce: number;
  fallback?: OmniMismatchFallbackNotice;
};

interface OmniTaskMismatchState {
  pendingErrorMismatch: PendingErrorMismatch | null;
  nextNonce: number;
  /** 当前能弹出错配引导的 Composer 实例 → 它可切换到的建议模式。 */
  consumers: Record<string, readonly OmniSuggestedVideoMode[]>;
  registerConsumer: (consumerId: string, modes: readonly OmniSuggestedVideoMode[]) => void;
  unregisterConsumer: (consumerId: string) => void;
  canConsume: (suggestedMode: OmniSuggestedVideoMode) => boolean;
  queueErrorMismatch: (
    suggestedMode: OmniSuggestedVideoMode,
    fallback?: OmniMismatchFallbackNotice
  ) => void;
  clearErrorMismatch: (nonce?: number) => void;
}

function canConsumeFrom(
  consumers: Record<string, readonly OmniSuggestedVideoMode[]>,
  suggestedMode: OmniSuggestedVideoMode
): boolean {
  return Object.values(consumers).some((modes) => modes.includes(suggestedMode));
}

function emitFallback(pending: PendingErrorMismatch | null): void {
  if (!pending?.fallback) return;
  pending.fallback.toast({
    title: pending.fallback.title,
    description: pending.fallback.description,
    variant: 'destructive',
  });
}

export const useOmniTaskMismatchStore = create<OmniTaskMismatchState>((set, get) => ({
  pendingErrorMismatch: null,
  nextNonce: 0,
  consumers: {},
  registerConsumer: (consumerId, modes) => {
    set((state) => {
      const previous = state.consumers[consumerId];
      if (
        previous &&
        previous.length === modes.length &&
        previous.every((mode, index) => mode === modes[index])
      ) {
        return state;
      }
      return { consumers: { ...state.consumers, [consumerId]: modes } };
    });
    const { pendingErrorMismatch, consumers } = get();
    if (pendingErrorMismatch && !canConsumeFrom(consumers, pendingErrorMismatch.suggestedMode)) {
      emitFallback(pendingErrorMismatch);
      set({ pendingErrorMismatch: null });
    }
  },
  unregisterConsumer: (consumerId) => {
    set((state) => {
      if (!(consumerId in state.consumers)) return state;
      const consumers = { ...state.consumers };
      delete consumers[consumerId];
      return { consumers };
    });
    const { pendingErrorMismatch, consumers } = get();
    if (pendingErrorMismatch && !canConsumeFrom(consumers, pendingErrorMismatch.suggestedMode)) {
      emitFallback(pendingErrorMismatch);
      set({ pendingErrorMismatch: null });
    }
  },
  canConsume: (suggestedMode) => canConsumeFrom(get().consumers, suggestedMode),
  queueErrorMismatch: (suggestedMode, fallback) =>
    set((state) => {
      const nonce = state.nextNonce + 1;
      return {
        nextNonce: nonce,
        pendingErrorMismatch: {
          suggestedMode,
          nonce,
          ...(fallback ? { fallback } : {}),
        },
      };
    }),
  clearErrorMismatch: (nonce) =>
    set((state) => {
      if (nonce != null && state.pendingErrorMismatch?.nonce !== nonce) return state;
      return { pendingErrorMismatch: null };
    }),
}));
